// src/pages/CategoryListPage.jsx

import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { Row, Col, Button, Form } from 'react-bootstrap';
import { FaFilter } from 'react-icons/fa';
import ProductCard from '../../../../components/user/shoppage/productcard/ProductCard';
import '../../../../assets/css/user/Equipmentcategory/categoryList/CategoryList.css';
import AppContext from '../../../../context/AppContext';
import axios from 'axios';

const CategoryList = () => {

  const location = useLocation();
  const { baseUrl, baseUrlForFile } = useContext(AppContext);

  const [selectedCategory, setSelectedCategory] = useState(
    location.state?.selectedCategory || 'All Products'
  );
  const [priceFilter, setPriceFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [equipments, setEquipments] = useState([]);

  const fetchAllEquipments = () => {
    axios.get(`${baseUrl}/customer/equipment/fetchAllEquipments`)
      .then((response) => {
        if (response.data.success == true) {
          setEquipments(response.data.equipments);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    fetchAllEquipments();
  }, []);

  useEffect(() => {
    const incoming = location.state?.selectedCategory;
    if (incoming) {
      setSelectedCategory(incoming);
      setCurrentPage(1);
    }
  }, [location.key]);

  // ── Helper: pick a display rate + unit from the 3 possible rates ──
  // Priority: hour → day → acre (jo bhi pehla mile wahi dikhaya jayega)
  const getDisplayRate = (equipment) => {
    if (equipment.rate_per_hour) {
      return { price: Number(equipment.rate_per_hour), unit: "hour" };
    }
    if (equipment.rate_per_day) {
      return { price: Number(equipment.rate_per_day), unit: "day" };
    }
    if (equipment.rate_per_acre) {
      return { price: Number(equipment.rate_per_acre), unit: "acre" };
    }
    return { price: 0, unit: "hour" };
  };

  // ✅ FIX: naye rate fields aur shop_name/shop_address accessor se use karna
  const allProducts = equipments?.map((equipment) => {
    const { price, unit } = getDisplayRate(equipment);
    return {
      id: equipment.id,

      // product card fields
      name: equipment.equipmentname,
      image: `${baseUrlForFile}/${equipment.image}`,
      price: price,
      priceUnit: unit,
      availability: equipment.status,
      category: equipment.category?.name || "",
      rating: equipment.avg_rating || 0,
      reviews: equipment.review_count || 0,
      shopName: equipment.shop_name || "Unknown Shop",
      shopAddress: equipment.shop_address || "",
      shopId: equipment.srrentaladmin_id,
    };
  }) ?? [];

  // Categories
  const allCategories = ['All Products', ...new Set(allProducts.map(p => p.category).filter(Boolean))];

  // Category count
  const getCategoryCount = (category) => {
    if (category === 'All Products') return allProducts.length;
    return allProducts.filter(p =>
      p.category?.toLowerCase().trim() === category?.toLowerCase().trim()
    ).length;
  };

  // Filter
  const filteredProducts = allProducts.filter(product => {
    const categoryMatch =
      selectedCategory === 'All Products' ||
      product.category?.toLowerCase().trim() === selectedCategory?.toLowerCase().trim();

    const availabilityMatch =
      availabilityFilter === 'all' ||
      (availabilityFilter === 'available' && product.availability === 'available') ||
      (availabilityFilter === 'rented' && product.availability === 'rented');

    let priceMatch = true;
    if (priceFilter === 'low') priceMatch = product.price < 2000;
    else if (priceFilter === 'medium') priceMatch = product.price >= 2000 && product.price < 5000;
    else if (priceFilter === 'high') priceMatch = product.price >= 5000;

    return categoryMatch && availabilityMatch && priceMatch;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  // Reset
  const resetFilters = () => {
    setSelectedCategory('All Products');
    setPriceFilter('all');
    setAvailabilityFilter('all');
    setCurrentPage(1);
  };

  return (
    <div className="category-list-page">

      {/* Header */}
      <div className="category-page-header-wrapper">
        <div className="category-page-header">
          <h1>Browse All Equipment by Category</h1>
          <p className="page-subtitle">
            Explore {allProducts.length} products from trusted rental shops
          </p>
        </div>
      </div>

      <div className="category-page-inner">

        {/* MOBILE FILTER BUTTON */}
        <div className="mobile-filter-btn-wrapper d-lg-none mb-3">
          <Button variant="primary" className="mobile-filter-btn" onClick={() => setShowMobileFilters(true)}>
            <FaFilter /> Filters
          </Button>
        </div>

        {/* MOBILE FILTER PANEL */}
        {showMobileFilters && (
          <div className="mobile-filters-overlay">
            <div className="mobile-filters-panel">
              <div className="mobile-filters-header">
                <h5>Filters</h5>
                <Button variant="light" className="close-btn" onClick={() => setShowMobileFilters(false)}>✕</Button>
              </div>

              <div className="cat-filter-section">
                <h6>Categories</h6>
                <div className="categories-list">
                  {allCategories.map(cat => (
                    <div
                      key={cat}
                      className={`category-item ${selectedCategory?.toLowerCase().trim() === cat?.toLowerCase().trim() ? 'active' : ''}`}
                      onClick={() => { setSelectedCategory(cat); setCurrentPage(1); }}
                    >
                      <span className="category-name">{cat}</span>
                      <span className="category-count">({getCategoryCount(cat)})</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cat-filter-section">
                <h6>Price Range</h6>
                <Form.Select value={priceFilter} onChange={(e) => { setPriceFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All Prices</option>
                  <option value="low">Under Rs. 2000</option>
                  <option value="medium">Rs. 2000 - 5000</option>
                  <option value="high">Above Rs. 5000</option>
                </Form.Select>
              </div>

              <div className="cat-filter-section">
                <h6>Availability</h6>
                <Form.Select value={availabilityFilter} onChange={(e) => { setAvailabilityFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All Status</option>
                  <option value="available">Available</option>
                  <option value="rented">Rented</option>
                </Form.Select>
              </div>

              <div className="mobile-filters-actions mt-3"></div>
            </div>
          </div>
        )}

        <Row className="mt-4">
          {/* LEFT SIDEBAR */}
          <Col xl="auto" lg={3} md={4} className="cat-filters-sidebar">
            <div className="filters-container1">

              <div className="cat-filters-header">
                <h4><FaFilter /> Filters</h4>
              </div>

              <div className="cat-filter-section">
                <h6>Categories</h6>
                <div className="categories-list">
                  {allCategories.map(cat => (
                    <div
                      key={cat}
                      className={`category-item ${selectedCategory?.toLowerCase().trim() === cat?.toLowerCase().trim() ? 'active' : ''}`}
                      onClick={() => { setSelectedCategory(cat); setCurrentPage(1); }}
                    >
                      <span className="category-name">{cat}</span>
                      <span className="category-count">({getCategoryCount(cat)})</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cat-filter-section">
                <h6>Price Range</h6>
                <Form.Select value={priceFilter} onChange={(e) => { setPriceFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All Prices</option>
                  <option value="low">Under Rs. 2000</option>
                  <option value="medium">Rs. 2000 - 5000</option>
                  <option value="high">Above Rs. 5000</option>
                </Form.Select>
              </div>

              <div className="cat-filter-section">
                <h6>Availability</h6>
                <Form.Select value={availabilityFilter} onChange={(e) => { setAvailabilityFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All Status</option>
                  <option value="available">Available</option>
                  <option value="rented">Rented</option>
                </Form.Select>
              </div>

            </div>
          </Col>

          {/* PRODUCTS */}
          <Col lg={9} md={8}>

            {currentProducts.length === 0 && (
              <div className="text-center py-5">
                <p style={{ fontFamily: "'Poppins', sans-serif", color: '#666' }}>
                  No products found for <strong>"{selectedCategory}"</strong>.
                </p>
                <Button onClick={resetFilters}
                  style={{ backgroundColor: '#FDB714', border: 'none', color: '#000', fontWeight: 600 }}>
                  Show All Products
                </Button>
              </div>
            )}

            <Row className="g-3">
              {currentProducts.map(product => (
                <Col key={product.id} lg={3} md={6} sm={12}>
                  <ProductCard
                    product={{
                      ...product,
                      isFavorite: product.isFavorite || false
                    }}
                    shopId={product.shopId}
                    shopName={product.shopName}
                    showShopName
                  />
                </Col>
              ))}
            </Row>

            {totalPages > 1 && (
              <div className="cat-pagination-wrapper">
                <ul className="cat-pagination">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <span className="page-link" onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}>Prev</span>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <li key={i + 1} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                      <span className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</span>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <span className="page-link" onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}>Next</span>
                  </li>
                </ul>
              </div>
            )}
          </Col>
        </Row>
      </div>

    </div>
  );
};

export default CategoryList;